import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AgentHistoryService } from '../agent-history.service';
import { AgentHistorySummary, AgentHistoryTranscript } from '../agent-history.model';
import { openTranscriptLink } from '../agent-transcript-link';
import { toolLabel } from '../agent-tool-result-format';

const PAGE_SIZE = 25;

// Read-only browser for the user's past conversations. Shows the list, or a single transcript once
// one is picked (back() returns to the list). No composer, no resume: a viewer, not a session.
@Component({
  selector: 'app-agent-history',
  templateUrl: './agent-history.component.html',
  styleUrls: ['./agent-history.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentHistoryComponent {
  private readonly history = inject(AgentHistoryService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);

  protected readonly items = signal<AgentHistorySummary[]>([]);
  protected readonly transcript = signal<AgentHistoryTranscript | null>(null);
  protected readonly turns = computed(() =>
    (this.transcript()?.turns ?? []).map((turn) => ({
      ...turn,
      toolCalls: turn.toolCalls.map((call) => ({ ...call, label: toolLabel(call.name) })),
    })),
  );
  protected readonly loading = signal(false);
  protected readonly errored = signal(false);
  private readonly continuationToken = signal<string | null>(null);
  protected readonly hasMore = computed(() => this.continuationToken() !== null);

  constructor() {
    this.fetch(undefined);
  }

  protected open(item: AgentHistorySummary): void {
    this.loading.set(true);
    this.errored.set(false);
    this.history
      .getTranscript(item.conversationId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (transcript) => {
          this.transcript.set(transcript);
          this.loading.set(false);
        },
        error: () => {
          this.errored.set(true);
          this.loading.set(false);
        },
      });
  }

  protected onContentClick(event: MouseEvent): void {
    openTranscriptLink(event, this.dialog, this.router);
  }

  protected back(): void {
    this.transcript.set(null);
    this.errored.set(false);
  }

  protected loadMore(): void {
    const token = this.continuationToken();
    if (token === null || this.loading()) {
      return;
    }
    this.fetch(token);
  }

  private fetch(continuationToken: string | undefined): void {
    this.loading.set(true);
    this.errored.set(false);
    this.history
      .list(PAGE_SIZE, continuationToken)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.items.update((current) => (continuationToken ? [...current, ...response.items] : response.items));
          this.continuationToken.set(response.continuationToken ?? null);
          this.loading.set(false);
        },
        error: () => {
          this.errored.set(true);
          this.loading.set(false);
        },
      });
  }
}
